import { ChangeDetectionStrategy, Component, DestroyRef, DOCUMENT, inject, LOCALE_ID, OnDestroy, OnInit, Renderer2, viewChild } from '@angular/core';
import { Settings } from 'luxon';

import { Platform } from '@angular/cdk/platform';
import { NavigationService } from '../@vex/services/navigation.service';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatIconRegistry, SafeResourceUrlWithIconOptions } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TRANSLOCO_LOADING_TEMPLATE, TranslocoService } from '@jsverse/transloco';
import { Observable, ObservedValueOf, Subject, Subscription, takeUntil, zip } from 'rxjs';
import { Setting } from '@eadsysteme/settingclient-v1';
import { DevService } from './services/dev.service';
import { PermissionsComponent } from './components/permissions/permissions.component';
import { SessionService } from './services/session.service';
import { Style, StyleService } from './services/style.service';
import { filter, map } from 'rxjs/operators';
import { NgcCookieConsentService, NgcNoCookieLawEvent, NgcStatusChangeEvent } from 'ngx-cookieconsent';
import { PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { RealEstateService, TenantUnitService } from '@eadsysteme/ewsrealestateclient-v2';
import { Icons } from './util/Icons';
import { register } from 'swiper/element/bundle';
import { DesignUtil } from './services/design-util.service';
import { SplashScreenService } from '../@vex/services/splash-screen.service';
import { MatDialog } from '@angular/material/dialog';
import { AraFeatureInDevelopmentDialogComponent } from './components/util/ara-feature-in-development-dialog/ara-feature-in-development-dialog.component';
import { PolicyDialogComponent } from './components/policy-dialog/policy-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomPoliciesService, CustomPolicy } from '@eadsysteme/portalsettingclient-v1';
import { LogoutService } from '../@vex/services/logout.service';

register();

@Component({
	selector: 'vex-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	providers: [
		{
			provide: TRANSLOCO_LOADING_TEMPLATE,
			useValue: '<p>loading...</p>',
		},
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
	title = 'customerPortal';

	deviceIdLength = 36;

	readonly permissionsComp = viewChild(PermissionsComponent);
	private settings$: Observable<Setting[]>;
	private readonly destroyRef = inject(DestroyRef);

	private readonly translocoService = inject(TranslocoService);
	private readonly renderer = inject(Renderer2);
	private readonly designUtil = inject(DesignUtil);
	private readonly platform = inject(Platform);
	private readonly ccService = inject(NgcCookieConsentService);
	private readonly router = inject(Router);
	private readonly document = inject(DOCUMENT);
	private readonly localeId = inject(LOCALE_ID);
	private readonly styleService = inject(StyleService);
	private readonly sessionService = inject(SessionService);
	private readonly route = inject(ActivatedRoute);
	private readonly navigationService = inject(NavigationService);
	private readonly matIconRegistry = inject(MatIconRegistry);
	private readonly devService = inject(DevService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly tenantUnitService = inject(TenantUnitService);
	private readonly customPoliciesService = inject(CustomPoliciesService);
	private readonly logoutService = inject(LogoutService);
	private readonly icons = inject(Icons);
	private readonly domSanitizer = inject(DomSanitizer);
	private readonly dialog = inject(MatDialog);
	private readonly splashScreenService = inject(SplashScreenService);

	// keep refs to subscriptions to be able to unsubscribe later
	private popupOpenSubscription!: Subscription;
	private popupCloseSubscription!: Subscription;
	private statusChangeSubscription!: Subscription;
	private revokeChoiceSubscription!: Subscription;
	private noCookieLawSubscription!: Subscription;

	private readonly destroy$ = new Subject<void>();

	constructor() {
		Settings.defaultLocale = this.localeId;

		if (this.platform.BLINK) {
			this.renderer.addClass(this.document.body, 'is-blink');
		}
	}

	ngOnInit(): void {
		this.registerIcons();

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd),
				takeUntil(this.destroy$)
			)
			.subscribe((event) => {
				sessionStorage.setItem('currentNavigation', event.url);
			});

		this.devService
			.getImpersonationEvent()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.setupPlatform();
			});

		this.designUtil.usePortalSettings(this.sessionService.effectiveArea).then(() => {
			this.setupPlatform();
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();

		this.popupOpenSubscription?.unsubscribe();
		this.popupCloseSubscription?.unsubscribe();
		this.statusChangeSubscription?.unsubscribe();
		this.revokeChoiceSubscription?.unsubscribe();
		this.noCookieLawSubscription?.unsubscribe();
	}

	private registerIcons() {
		this.matIconRegistry.addSvgIconResolver((name: string, namespace: string): SafeResourceUrl | SafeResourceUrlWithIconOptions | null => {
			switch (namespace) {
				case 'mat':
					return this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/img/icons/material-design-icons/outlined/${name}.svg`); // NOSONAR: Make sure disabling Angular built-in sanitization is safe here.

				case 'logo':
					return this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/img/icons/logos/${name}.svg`);

				case 'flag':
					return this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/img/icons/flags/${name}.svg`);

				case 'ara':
					return this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/img/icons/arasys/${name}.svg`);
			}
			return null;
		});
	}

	private setupPlatform() {
		this.handleCookieConsent();
		this.sessionService.userHasPermission.next(false);
		Settings.defaultLocale = this.localeId;
		this.translateSideNav();
		this.subscribePermissionsChange();
		if (this.platform.BLINK) {
			this.renderer.addClass(this.document.body, 'is-blink');
		}
		this.route.queryParamMap
			.pipe(map((queryParamMap) => queryParamMap.has('rtl') && coerceBooleanProperty(queryParamMap.get('rtl'))))
			.subscribe((isRtl) => {
				this.document.body.dir = isRtl ? 'rtl' : 'ltr';
			});

		this.route.queryParamMap
			.pipe(filter((queryParamMap) => queryParamMap.has('style')))
			.subscribe((queryParamMap) => this.styleService.setStyle(queryParamMap.get('style') as Style));

		this.routeForRightOrEntity();
		this.handlePolicies();
	}

	private handleCookieConsent() {
		// subscribe to cookieconsent observables to react to main events
		this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(() => {
			// you can use this.ccService.getConfig() to do stuff...
			// console.log('Cookie Popup opened');
		});

		this.popupCloseSubscription = this.ccService.popupClose$.subscribe(() => {
			// you can use this.ccService.getConfig() to do stuff...
			document.querySelectorAll('[aria-label="cookieconsent"]').forEach((element) => {
				element.remove();
			});
		});

		this.statusChangeSubscription = this.ccService.statusChange$.subscribe((event: NgcStatusChangeEvent) => {
			// you can use this.ccService.getConfig() to do stuff...
			// console.log('Cookie status changed');
		});

		this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(() => {
			// you can use this.ccService.getConfig() to do stuff...
		});

		this.noCookieLawSubscription = this.ccService.noCookieLaw$.subscribe((event: NgcNoCookieLawEvent) => {
			// you can use this.ccService.getConfig() to do stuff...
		});
	}

	private handlePolicies() {
		const allAreas = this.sessionService.tokenAreas();
		if (!allAreas.length) {
			return;
		}
		for (const area of allAreas) {
			this.customPoliciesService.getNecessaryConsents(area).subscribe({
				next: (newPolicies) => {
					if (newPolicies?.length < 1) {
						return;
					}
					this.openPolicyDialog(newPolicies, area);
				},
			});
		}
	}

	private openPolicyDialog(newPolicies: Array<CustomPolicy>, area: number) {
		const dialogRef = this.dialog.open(PolicyDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.policies = newPolicies;
		dialogRef.componentInstance.serviceCompanyArea = area;
		dialogRef.componentInstance.logoutMode = true;
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: { consented: boolean }) => {
				if (!result.consented) {
					this.logoutService.logout();
				}
			});
	}

	private routeForRightOrEntity() {
		const selectedDeviceId = sessionStorage.getItem('selectedDeviceId');

		if (!this.sessionService.isPropertyManagement()) {
			this.sessionService.getAllPermissionsOfLoggedInUser().subscribe({
				next: (permissionObjects) => {
					if (permissionObjects.length === 0) {
						// no permissions? Route to permissions!
						this.sessionService.userHasPermission.next(false);
						this.router.navigate(['/permissions']);
					} else {
						// only one permission? route to uvi as Resident, detailView as Owner
						this.sessionService.userHasPermission.next(true);
						if (permissionObjects.length === 1) {
							this.routeToSingleEntity(permissionObjects[0]);
						} else {
							this.router.navigate(['/dashboard']);
						}
					}
				},
				error: () => {
					this.sessionService.userHasPermission.next(false);
					this.router.navigate(['/permissions']);
				},
			});
		} else {
			this.sessionService.userHasPermission.next(true);
			if (selectedDeviceId?.length === this.deviceIdLength) {
				this.router.navigateByUrl('/devices/' + selectedDeviceId);
			}
		}
	}

	private routeToSingleEntity(permissionObject: PermissionObject) {
		const area = permissionObject.area;
		const tenantUnitId = permissionObject.tenantUnitId;
		const permissionType = permissionObject.permissionType;
		// TODO Read correct TenantUnit Here!!!
		this.realEstateService.getRealEstateByTenantUnitExportId(area, tenantUnitId).subscribe((realEstate) => {
			this.tenantUnitService.getTenantUnitById(area, realEstate.id, tenantUnitId).subscribe({
				next: (tenantUnit) => {
					if (permissionType === PermissionObject.PermissionTypeEnum.OWNER) {
						this.routeToTenantUnitDetails(tenantUnitId, realEstate.id);
					} else if (permissionType === PermissionObject.PermissionTypeEnum.RESIDENT) {
						this.routeToUvi(tenantUnit.id, realEstate.id);
					} else if (permissionType === PermissionObject.PermissionTypeEnum.PROPERTY_MANAGEMENT) {
						this.routeToRealEstate(realEstate.id);
					} else {
						// unknown role, should not happen -> route to permissions
						this.router.navigate(['/permissions']);
					}
				},
				error: (err) => {
					if (this.sessionService.isDevRole()) {
						console.error('Error - Could not navigate to TenantUnit!', err);
					}
				},
			});
		});
	}

	private routeToRealEstate(realEstateId: string) {
		this.router.navigate(['realestates/' + realEstateId]);
	}

	private routeToTenantUnitDetails(tenantUnitId: string, realEstateId: string) {
		this.router.navigate(['realestates/' + realEstateId + '/tenantunits/' + tenantUnitId]);
	}

	private routeToUvi(tenantUnitId: string, realEstateId: string) {
		this.router.navigate(['realestates/' + realEstateId + '/tenantunits/' + tenantUnitId + '/uvi']);
	}

	private subscribePermissionsChange() {
		this.sessionService.userHasPermission.asObservable().subscribe(async (userHasPermissions) => {
			if (userHasPermissions) {
				await this.sessionService.updateSession(false);
				await this.translateSideNav(true);
			}
		});
	}

	private async translateSideNav(refreshNavItems: boolean = false) {
		zip(
			this.translocoService.selectTranslate('sideboard.dashboard'),
			this.translocoService.selectTranslate('sideboard.realestates'),
			this.translocoService.selectTranslate('permissions.permissions'),
			this.translocoService.selectTranslate('settings.settings'),
			this.translocoService.selectTranslate('sideboard.massPrint'),
			this.translocoService.selectTranslate('development.development'),
			this.translocoService.selectTranslate('smokeDetectorCheck.smokeDetectorChecks'),
			this.translocoService.selectTranslate('sideboard.billings'),
			this.translocoService.selectTranslate('sideboard.menuBilling'),
			this.translocoService.selectTranslate('sideboard.menuBuildingUsersConsumption'),
			this.translocoService.selectTranslate('sideboard.menuHelpSettings'),
			this.translocoService.selectTranslate('sideboard.menuMailbox'),
			this.translocoService.selectTranslate('documentTransfer.title'),
			this.translocoService.selectTranslate('documentTransfer.archive'),
			this.translocoService.selectTranslate('sideboard.araApp'),
			this.translocoService.selectTranslate('sideboard.benutzerverwaltung')
		)
			.pipe(map((value) => value))
			.subscribe((translatedLabel) => {
				const navItems: any = this.navigationService.items.filter((navItem: any) => {
					return navItem.route !== '';
				});
				if (!refreshNavItems && navItems.length > 0) {
					for (const element of navItems) {
						const currentNavItem = element;
						switch (currentNavItem.route) {
							case '/dashboard':
								currentNavItem.label = translatedLabel[0];
								break;
							case '/realestates':
								currentNavItem.label = translatedLabel[1];
								break;
							case '/permissions':
								currentNavItem.label = translatedLabel[2];
								break;
							case '/settings':
								currentNavItem.label = translatedLabel[3];
								break;
							case '/mass-printing':
								currentNavItem.label = translatedLabel[4];
								break;
							case '/development':
								currentNavItem.label = translatedLabel[5];
								break;
							case '/ara-app':
								currentNavItem.label = translatedLabel[14];
								break;
						}
					}
					this.reload(this.router.url);
				} else if (refreshNavItems) {
					this.navigationService.items = [];
					this.createSideMenu(translatedLabel);
					this.navigationService.items$.next(this.navigationService.items);
				} else {
					this.createSideMenu(translatedLabel);
				}
			});
	}

	private createSideMenu(translatedLabel: ObservedValueOf<Observable<any>>[]) {
		const addDropdownChildren = (labelKey: string, children: any[]) => {
			const translatedLabel = this.translocoService.translate(labelKey);
			const dropdownItem = this.navigationService.items.find((item) => item.label === translatedLabel && item.type === 'dropdown');

			if (dropdownItem && 'children' in dropdownItem) {
				dropdownItem.children.push(...children);
			}
		};

		this.navigationService.items.push({
			type: 'dropdown',
			label: 'Dashboards',
			icon: this.icons.get('dashboard'),
			children: [
				{
					type: 'link',
					label: translatedLabel[0],
					route: '/dashboard',
					icon: this.icons.get('dashboard'),
				},
			],
		});

		if (this.sessionService.userHasPermission.getValue()) {
			// buildings, users and consumption
			this.navigationService.items.push({
				type: 'dropdown',
				label: translatedLabel[9],
				children: [
					{
						type: 'link',
						label: translatedLabel[1],
						route: '/realestates',
						icon: this.icons.get('personGroup'),
					},
					// Die Verbrauchsinformation ist der Hausverwaltung vorbehalten.
					...(this.sessionService.hasPropertyManagementPermission()
						? [
								{
									type: 'link' as const,
									label: translatedLabel[4],
									route: '/mass-printing',
									icon: this.icons.get('consumptionInfo'),
								},
							]
						: []),
				],
			});
		}

		if (this.sessionService.isDevRole()) {
			// billings
			this.navigationService.items.push({
				type: 'dropdown',
				label: translatedLabel[8],
				soon: true,
				children: [
					{
						type: 'link',
						label: translatedLabel[7],
						route: () => this.openFeatureInDevelopmentDialog('/billing-units'),
						icon: this.icons.get('house_euro'),
						soon: true,
					},
				],
			});

			// mailbox
			this.navigationService.items.push({
				type: 'dropdown',
				label: translatedLabel[11],
				soon: true,
				children: [
					{
						type: 'link',
						label: translatedLabel[12],
						route: () => this.openFeatureInDevelopmentDialog('/document-transfer'),
						icon: this.icons.get('syncAlt'),
						soon: true,
					},
					{
						type: 'link',
						label: translatedLabel[13],
						route: () => this.openFeatureInDevelopmentDialog('/document-transfer/archive'),
						icon: this.icons.get('archive'),
						soon: true,
					},
				],
			});
		}

		// help & settings
		this.navigationService.items.push({
			type: 'dropdown',
			alignBottom: true,
			label: translatedLabel[10],
			children: [
				{
					type: 'link',
					label: translatedLabel[14],
					route: '/ara-app',
					img: 'assets/img/icons/araproducts/araApp/araApp_weiß_sidenav.svg',
					icon: this.icons.get('family'),
				},
				{
					type: 'link',
					label: translatedLabel[2],
					route: '/permissions',
					icon: this.icons.get('holidayVillage'),
				},
				{
					type: 'link',
					label: translatedLabel[15],
					route: '/user-management',
					icon: this.icons.get('manageAccounts'),
				},
				{
					type: 'link',
					label: translatedLabel[5],
					route: '/development',
					icon: this.icons.get('development'),
				},
				{
					type: 'link',
					label: translatedLabel[3],
					route: '/settings',
					icon: this.icons.get('settings'),
				},
			],
		});
	}

	private openFeatureInDevelopmentDialog(targetRoute?: string): void {
		this.dialog.open(AraFeatureInDevelopmentDialogComponent, {
			width: '450px',
			autoFocus: false,
			data: { targetRoute },
		});
	}

	async reload(url: string): Promise<boolean> {
		return this.router.navigateByUrl(url);
	}
}
